/**
 * Access Restricted — rendered when a user's session is valid but their
 * email isn't approved for the Lens operator allowlist (auth-gate.js).
 *
 * Why this exists (P-019 lineage):
 *   The first version redirected to cleic.com with `?lens_private_alpha=true`,
 *   but cleic.com didn't handle the param and the user's browser session was
 *   still active there → infinite-loop between subdomains.
 *
 * The fix: render the rejection IN lens.cleic.com itself. No cross-origin
 * coordination needed; user gets a clear message + clean exit.
 *
 * Tailwind tokens: this file uses Lens's palette (bg-bg / text-text /
 * text-text-muted / border-border / accent-blue / surface / surface-el),
 * NOT Ledger's semantic tokens (paper / ink / mute / line / accent). The
 * visual result intentionally mirrors Ledger's AccessRestricted card so
 * the two apps feel like one product family.
 */
export function AccessRestricted({ email }) {
  // No auto-signOut, no forced sign-out on navigation. The user's identity
  // is fine for cleic.com — they just don't have Lens private-alpha access.
  // cleic.com handles the lens_private_alpha notice + suppresses auto-redirect.

  function handleBackToCleic() {
    window.location.replace('https://cleic.com?lens_private_alpha=true');
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6 text-text">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-blue text-sm font-bold text-white">
            C
          </div>
          <span className="text-sm font-bold text-text">Cleic Lens</span>
        </div>

        <h1 className="text-xl font-bold text-text">
          Lens access is restricted
        </h1>
        <p className="mt-2 text-sm text-text-muted">
          {email ? (
            <>
              The account <strong className="text-text">{email}</strong> isn't
              on the Cleic Lens operator allowlist. Lens remains restricted to
              approved operators and test accounts.
            </>
          ) : (
            <>
              This signed-in account isn't on the Cleic Lens operator allowlist.
            </>
          )}
        </p>

        <div className="mt-6 rounded-lg border border-border bg-surface-el p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent-blue">
            Try this
          </p>
          <ul className="mt-2 space-y-1 text-xs text-text-muted">
            <li>• Sign in with a different Google account that's on your
              operator allowlist.</li>
            <li>• Use the Ledger-approved account when opening Lens from a
              Book-scoped Ledger session.</li>
            <li>• Questions?{' '}
              <a
                href="mailto:support@cleic.com"
                className="text-accent-blue underline hover:opacity-80"
              >
                support@cleic.com
              </a>
            </li>
          </ul>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={handleBackToCleic}
            className="rounded-md bg-accent-blue px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition-opacity"
          >
            Back to cleic.com
          </button>
        </div>

        <p className="mt-8 text-center text-[10px] text-text-muted">
          Cleic Lens · Restricted Access
        </p>
      </div>
    </div>
  );
}
